import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url';
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import type * as H from 'history';
import { environments } from '../../config-custom/environments';
import { isServer } from './utils';
import type { EnvName, EnvProps } from '../../config-custom/environments';
import type { Store } from '../bff-api/stores/model';

export const getDeployTarget = (): string | undefined =>
    isServer ? process.env.DEPLOY_TARGET : getConfig().deployTarget;

export const getEnv = (): EnvProps => {
    const deployTarget = isServer
        ? process.env.DEPLOY_TARGET
        : // Do not refactor, if calling getConfig() server side, it ends up in circular dependency
          getConfig().deployTarget;

    const envName = (
        deployTarget && deployTarget in environments ? deployTarget : 'development'
    ) as EnvName;

    return environments[envName];
};

export const getIsProduction = (): boolean => getDeployTarget() === 'production';

export const getOriginUrl = (): string =>
    typeof window === 'undefined' ? getEnv().originUrl : getAppOrigin() || '';

export const redirect = (history: H.History, url: string): void => {
    if (url === '/checkout' || url.startsWith('/checkout/') || url.startsWith('/account/')) {
        history.push(url);
    } else {
        // eslint-disable-next-line no-restricted-globals
        location.assign(url);
    }
};

export const toGoogleMapsQuery = (store?: Partial<Store> | null): string => {
    if (!store || !store.id) {
        return '';
    }

    const query = [];
    if (store.address1) {
        query.push(store.address1);
    }
    if (store.address2) {
        query.push(store.address2);
    }
    if (store.city) {
        query.push(store.city);
    }
    if (store.postalCode) {
        query.push(store.postalCode);
    }
    if (store.stateCode) {
        query.push(store.stateCode);
    }
    if (store.countryCode) {
        query.push(store.countryCode);
    }

    return encodeURI(query.join(', '));
};

export const argsToQueryString = (args: string[]): string => {
    let isKey = true;
    return args
        .map((arg) => {
            isKey = !isKey;
            return isKey ? `=${encodeURIComponent(arg)}` : `&${encodeURIComponent(arg)}`;
        })
        .join('')
        .slice(1);
};

export const toGoogleMapsURL = (store?: Partial<Store> | null): string => {
    const query = toGoogleMapsQuery(store);
    return `http://maps.google.com/maps?hl=en&f=q&q=${query}`;
};

export type HttpParams = Record<string, string | number | boolean | string[]>[];

export function getQueryString(queryParams: HttpParams): string {
    return queryParams
        .map((record) =>
            Object.entries(record)
                .map((entry) => {
                    const key = entry[0];
                    const value = entry[1];

                    return !Array.isArray(value)
                        ? `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
                        : value
                              .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
                              .join('&');
                })
                .join('&'),
        )
        .join('&');
}

export function toRelativeUrl(absoluteUrl: string): string {
    try {
        const url = new URL(absoluteUrl);
        return url.pathname + url.search + url.hash;
    } catch (error) {
        return absoluteUrl;
    }
}

export const isTestEnvironment = (): boolean =>
    typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

export const isLocationOnMobifyHost = (): boolean =>
    !isServer &&
    !isTestEnvironment() &&
    window.location.hostname.endsWith('.mobify-storefront.com');

export const getMobifyProxyPrefix = (): string =>
    isLocationOnMobifyHost() ? '/mobify/proxy/ocapi' : '';

export const getQueryStringFromRequest = (req: { query?: unknown }): string => {
    if (!req?.query || typeof req.query !== 'object') {
        return '';
    }
    const src = req.query as Record<string, unknown>;
    const safeRecord: Record<string, string | number | boolean | string[]> = {};
    Object.keys(src).forEach((key) => {
        const value = src[key];
        if (Array.isArray(value)) {
            const filtered = value
                .filter(
                    (v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean',
                )
                .map((v) => String(v));
            if (filtered.length > 0) {
                safeRecord[key] = filtered;
            }
        } else if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            safeRecord[key] = value as string | number | boolean;
        }
    });
    const qsString = getQueryString([safeRecord]);
    return qsString ? `?${qsString}` : '';
};
